import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useColorMode } from "@chakra-ui/react";

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');`;

const BRAND = "#028EC4";
const AMBER = "#FFA900";

/* ─── IMAGES ───────────────────────────────────────────────────────────────── */
const IMAGES = {
  hero:      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=70",
  classroom: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1200&q=70",
  group:     "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=70",
  workshop:  "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=70",
};

/* ─── ICON SET (inline SVG, no extra dependency) ───────────────────────────── */
const ICONS = {
  building: <><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-6h6v6" /><path d="M9 10h.01M15 10h.01" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
  calculator: <><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" /></>,
  code: <><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></>,
  terminal: <><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>,
  globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  award: <><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></>,
  check: <polyline points="20 6 9 17 4 12" />,
  chart: <><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>,
  clipboard: <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M9 14l2 2 4-4" /></>,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  monitor: <><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>,
  mail: <><path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" /><polyline points="22 6 12 13 2 6" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  minus: <line x1="5" y1="12" x2="19" y2="12" />,
  book: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
  mic: <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></>,
  video: <><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></>,
  cpu: <><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" /></>,
  cloud: <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />,
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  layout: <><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>,
  pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  quote: <><path d="M3 21c3 0 7-1 7-8V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3c0 3-1 4-4 4z" /><path d="M15 21c3 0 7-1 7-8V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3c0 3-1 4-4 4z" /></>,
  star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
};

const Icon = ({ name, size = 20, color = "currentColor", sw = 1.8, fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    {ICONS[name]}
  </svg>
);

/* ─── DATA ─────────────────────────────────────────────────────────────────── */
const STATS = [
  { icon: "building", v: "10+", l: "College tie-ups" },
  { icon: "users", v: "700+", l: "Students trained" },
  { icon: "layers", v: "15+", l: "Domains offered" },
  { icon: "clock", v: "60–90", l: "Days per program" },
];

const AUDIENCE = ["Engineering colleges", "Degree colleges", "MCA and PG programs", "Final-year placement batches"];

const PILLARS = [
  { icon: "zap", title: "Current curriculum", desc: "Refreshed every batch with the tools, frameworks and practices companies are hiring for right now." },
  { icon: "target", title: "Built for the job market", desc: "Designed backwards from real job descriptions, so students learn what recruiters actually test." },
  { icon: "calculator", title: "Aptitude and reasoning", desc: "Quantitative, logical and verbal practice that prepares students for the first round of placement drives." },
  { icon: "terminal", title: "Core CS fundamentals", desc: "DSA, DBMS, OS, Networks and OOP taught through practical examples rather than theory alone." },
  { icon: "briefcase", title: "Project-based learning", desc: "Every student builds real, demonstrable projects that strengthen their resume and portfolio." },
  { icon: "chart", title: "Measured outcomes", desc: "Regular tests and assessments give faculty a clear, data-backed view of each batch's progress." },
];

const PHASES = [
  { icon: "book", title: "Foundation", desc: "Programming basics, aptitude and core CS revision to bring the whole batch to a common level." },
  { icon: "code", title: "Core technical track", desc: "The chosen domain is taught through guided labs, live coding and weekly assignments." },
  { icon: "clipboard", title: "Projects and assessments", desc: "Capstone projects, module tests and mock online assessments that mirror company drives." },
  { icon: "mic", title: "Placement readiness", desc: "Resume building, mock technical interviews, group discussions and HR round practice." },
];

const MODES = [
  {
    icon: "monitor", title: "Online training", tone: "brand",
    desc: "Live interactive sessions with recorded backups, assignments and doubt clearing. Ideal for multi-campus or remote batches.",
    points: ["Live classes with recordings", "Online tests and assessments", "Dedicated doubt-clearing sessions", "Works across multiple campuses"],
  },
  {
    icon: "pin", title: "On-campus training", tone: "outline",
    desc: "Our tutors are deployed at your campus and train students in person, on a schedule that fits your timetable.",
    points: ["Tutors deployed on campus", "Hands-on lab sessions", "In-person mentoring", "Scheduled around your timetable"],
  },
];

const DOMAIN_GROUPS = [
  { icon: "code", title: "Software development", items: ["Full Stack Java", "MERN Stack", "Python Programming", "App Development"] },
  { icon: "cpu", title: "Data and AI", items: ["Data Science", "Machine Learning & AI", "Generative AI", "Data Analytics"] },
  { icon: "cloud", title: "Cloud and infrastructure", items: ["Cloud Computing (AWS)", "DevOps", "IoT & Embedded"] },
  { icon: "shield", title: "Security and quality", items: ["Cyber Security", "Software Testing"] },
  { icon: "layout", title: "Design", items: ["UI/UX Design"] },
  { icon: "book", title: "Foundations", items: ["Data Structures & Algorithms", "Aptitude & Soft Skills"] },
];

const DELIVERABLES = [
  { icon: "award", title: "Student certificates", desc: "Every student who completes the program is certified." },
  { icon: "file", title: "Faculty progress reports", desc: "Regular updates on attendance, test scores and project status." },
  { icon: "chart", title: "Assessment scorecards", desc: "Batch-level results your Training & Placement cell can act on." },
  { icon: "briefcase", title: "Project showcase", desc: "Completed projects students can present to recruiters." },
  { icon: "mic", title: "Interview guidance", desc: "Mock interviews and career support after the training ends." },
  { icon: "users", title: "One coordinator", desc: "A single point of contact for scheduling and faculty coordination." },
];

const STEPS = [
  { icon: "phone", title: "Book a call", desc: "Tell us about your college, branches, batch size and placement goals." },
  { icon: "calendar", title: "Plan the program", desc: "We select domains, the mode (online or on campus) and a 60–90 day schedule that fits your academic calendar." },
  { icon: "video", title: "Train and assess", desc: "Sessions begin, with regular tests, projects and progress reports shared with your faculty." },
  { icon: "award", title: "Certify and support", desc: "Students receive certificates and guidance for interviews and placements after training." },
];

const TESTIMONIALS = [
  { name: "Dr. K. Ramesh", role: "Head of CSE, Engineering College", quote: "Students came out of the 90-day program ready for technical rounds. The curriculum is current." },
  { name: "Prof. S. Lakshmi", role: "Training & Placement Officer", quote: "Aptitude plus coding in one program made our placement prep far more organised." },
  { name: "Harsha Vardhan", role: "B.Tech Final Year", quote: "The tutors on campus explained DSA in a way that finally clicked for me." },
  { name: "Sneha Reddy", role: "B.Tech Third Year", quote: "I built real projects in 60 days. My resume looks completely different now." },
  { name: "Prof. M. Srinivas", role: "Dean, Computer Applications", quote: "Well-planned schedule that did not disturb our regular classes. Faculty coordination was smooth." },
  { name: "Pavan Kumar", role: "MCA Student", quote: "Online sessions were interactive and the recordings helped me revise before interviews." },
  { name: "Divya Sri", role: "B.Tech Final Year", quote: "Mock tests and aptitude training helped me clear my first company's online assessment." },
  { name: "Dr. A. Prasad", role: "Principal, Degree College", quote: "Professional team, transparent process and students who are actually more confident." },
];

const FAQS = [
  { q: "Will the program disturb regular classes?", a: "No. We plan the 60–90 day schedule around your academic calendar, timetable and exam periods, and agree it with your faculty before the first session." },
  { q: "Can we choose more than one domain?", a: "Yes. You can choose a single domain or combine several, and we can customise the track for each branch or batch." },
  { q: "Do you offer both online and on-campus delivery?", a: "Yes. Online batches run as live sessions with recordings. For on-campus delivery, our tutors are deployed at your college and train students in person." },
  { q: "How will faculty track student progress?", a: "Regular tests, assignments and projects are scored, and progress reports are shared with your faculty and Training & Placement cell throughout the program." },
  { q: "What do students receive at the end?", a: "Students receive a completion certificate, a portfolio of projects they built, and guidance for interviews and placements after the training." },
  { q: "How do we get started?", a: "Book a campus demo or an appointment using the buttons on this page, or email trainings@grappl-tech.in with your branches, batch size and goals." },
];

/* ─── SMALL PIECES ─────────────────────────────────────────────────────────── */
const IconTile = ({ name, color = BRAND, size = 46 }) => (
  <div style={{ width: size, height: size, borderRadius: 12, background: `${color}1A`, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <Icon name={name} size={size * 0.46} />
  </div>
);

const Photo = ({ src, alt, ratio = "4 / 3", style }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="cx-photo" style={{ aspectRatio: ratio, ...style }}>
      {!failed && <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }} />}
    </div>
  );
};

const BookButton = ({ variant = "yellow", label = "Book Your Appointment" }) => (
  <a href="https://tally.so/r/obeQab" target="_blank" rel="noopener noreferrer" className={`cx-btn cx-btn-${variant}`}>
    {label} <Icon name="arrow" size={16} sw={2.4} />
  </a>
);

const SectionHead = ({ title, lead, center = false }) => (
  <div style={{ textAlign: center ? "center" : "left", marginBottom: 40 }}>
    <h2 className="cx-h2">{title}</h2>
    {lead && <p className="cx-lead" style={center ? { margin: "0 auto" } : undefined}>{lead}</p>}
  </div>
);

const FaqItem = ({ q, a, open, onToggle }) => (
  <div className="cx-faq-item" data-open={open}>
    <button className="cx-faq-q" onClick={onToggle} aria-expanded={open}>
      <span>{q}</span>
      <Icon name={open ? "minus" : "plus"} size={18} color={BRAND} sw={2.2} />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
          <p className="cx-faq-a">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function CollegeTrainings() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const buttonRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(0);

  // Google Calendar booking button (unchanged)
  useEffect(() => {
    if (document.getElementById("google-calendar-button")) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://calendar.google.com/calendar/scheduling-button-script.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://calendar.google.com/calendar/scheduling-button-script.js";
    script.async = true;
    script.onload = () => {
      if (window.calendar && buttonRef.current) {
        window.calendar.schedulingButton.load({
          url: "https://calendar.google.com/calendar/appointments/schedules/AcZssZ2KGwxgLYJI73sTiS81PebrfLUWoW04b4l7aoIbszfdnTwvw43XH2-LtnmV_0haia3ko2NzUPX-?gv=true",
          color: BRAND,
          label: "Book a Campus Demo",
          target: buttonRef.current,
        });
      }
    };
    document.body.appendChild(script);
  }, []);

  // Design tokens (exposed as CSS variables so the stylesheet stays simple)
  const vars = {
    "--bg":      isDark ? "#0A0B12" : "#F6F8FB",
    "--alt":     isDark ? "#0E1019" : "#EEF3F8",
    "--card":    isDark ? "#151722" : "#FFFFFF",
    "--border":  isDark ? "rgba(255,255,255,0.09)" : "#E2E8F0",
    "--text":    isDark ? "rgba(255,255,255,0.94)" : "#0F172A",
    "--muted":   isDark ? "rgba(255,255,255,0.58)" : "#526071",
    "--shadow":  isDark ? "0 10px 30px rgba(0,0,0,0.35)" : "0 10px 30px rgba(15,23,42,0.07)",
    "--brand":   BRAND,
    "--amber":   AMBER,
    "--navy":    "#06324A",
  };

  return (
    <>
      <style>{`
        ${FONT_LINK}
        .cx, .cx * { box-sizing: border-box; }
        .cx { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--text); min-height: 100vh; overflow-x: hidden; }
        .cx h1, .cx h2, .cx h3, .cx .cx-display { font-family: 'Bricolage Grotesque', sans-serif; letter-spacing: -0.02em; }
        .cx-wrap { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
        .cx-section { padding: 88px 0; }
        .cx-alt { background: var(--alt); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
        .cx-h2 { font-size: clamp(1.7rem, 3vw, 2.4rem); font-weight: 800; line-height: 1.15; margin: 0 0 14px; color: var(--text); }
        .cx-lead { color: var(--muted); font-size: 16px; line-height: 1.75; max-width: 640px; margin: 0; }
        .cx-body { color: var(--muted); font-size: 15.5px; line-height: 1.8; margin: 0 0 16px; }
        .cx-photo { width: 100%; border-radius: 20px; overflow: hidden; border: 1px solid var(--border); box-shadow: var(--shadow); background: linear-gradient(135deg, rgba(2,142,196,0.25), rgba(255,169,0,0.2)); }

        /* buttons */
        .cx-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-weight: 700; font-size: 14.5px; padding: 13px 24px; border-radius: 12px; text-decoration: none; border: 1px solid transparent; transition: transform .15s, filter .2s, background .2s; cursor: pointer; }
        .cx-btn:hover { filter: brightness(1.07); transform: translateY(-1px); }
        .cx-btn:focus-visible, .cx-faq-q:focus-visible { outline: 3px solid ${AMBER}; outline-offset: 3px; }
        .cx-btn-yellow { background: ${AMBER}; color: #111; }
        .cx-btn-blue { background: ${BRAND}; color: #fff; box-shadow: 0 8px 22px rgba(2,142,196,0.35); }
        .cx-btn-ghost { background: transparent; color: var(--brand); border-color: rgba(2,142,196,0.45); }
        .cx-btn-white { background: #fff; color: var(--navy); }
        .cx-btn-line { background: transparent; color: #fff; border-color: rgba(255,255,255,0.45); }

        /* hero */
        .cx-hero { position: relative; padding: 84px 0 72px; border-bottom: 1px solid var(--border);
          background:
            radial-gradient(ellipse 70% 60% at 85% 0%, rgba(2,142,196,${isDark ? 0.2 : 0.12}) 0%, transparent 65%),
            linear-gradient(var(--border) 1px, transparent 1px) 0 0 / 100% 56px,
            var(--bg); }
        .cx-hero-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 56px; align-items: center; }
        .cx-pill { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--brand); background: rgba(2,142,196,0.1); border: 1px solid rgba(2,142,196,0.3); padding: 7px 14px; border-radius: 999px; margin-bottom: 22px; }
        .cx-hero h1 { font-size: clamp(2.5rem, 5.6vw, 4.4rem); font-weight: 800; line-height: 1.04; margin: 0 0 20px; }
        .cx-hero-sub { font-size: clamp(15px, 1.8vw, 18px); color: var(--muted); line-height: 1.75; max-width: 560px; margin: 0 0 28px; }
        .cx-audience { display: flex; flex-wrap: wrap; gap: 10px 18px; margin: 0 0 30px; padding: 0; list-style: none; }
        .cx-audience li { display: flex; align-items: center; gap: 7px; font-size: 14px; font-weight: 500; color: var(--text); }
        .cx-cta-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
        .cx-hero-visual { position: relative; }
        .cx-float { position: absolute; left: -28px; bottom: -28px; background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 16px 18px; box-shadow: var(--shadow); display: flex; align-items: center; gap: 14px; max-width: 290px; }
        .cx-float-badge { position: absolute; top: 18px; right: -14px; background: ${AMBER}; color: #111; font-weight: 700; font-size: 13px; padding: 9px 14px; border-radius: 12px; display: flex; align-items: center; gap: 7px; box-shadow: var(--shadow); }

        /* stat strip */
        .cx-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 20px; overflow: hidden; margin-top: 64px; box-shadow: var(--shadow); }
        .cx-stat { background: var(--card); padding: 24px; display: flex; align-items: center; gap: 16px; }
        .cx-stat-v { font-family: 'Bricolage Grotesque', sans-serif; font-size: 1.9rem; font-weight: 800; line-height: 1; }
        .cx-stat-l { font-size: 13px; color: var(--muted); margin-top: 4px; }

        /* pillars: shared-border grid */
        .cx-ledger { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 20px; overflow: hidden; }
        .cx-pillar { background: var(--card); padding: 30px 28px; transition: background .2s; }
        .cx-pillar:hover { background: rgba(2,142,196,${isDark ? 0.08 : 0.04}); }
        .cx-pillar h3 { font-size: 1.1rem; font-weight: 700; margin: 18px 0 8px; }
        .cx-pillar p { margin: 0; font-size: 14.5px; line-height: 1.7; color: var(--muted); }

        /* phases */
        .cx-phases { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
        .cx-phase-top { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
        .cx-phase-num { width: 44px; height: 44px; border-radius: 50%; background: ${BRAND}; color: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Bricolage Grotesque', sans-serif; font-weight: 800; font-size: 17px; flex-shrink: 0; box-shadow: 0 0 0 6px rgba(2,142,196,0.15); }
        .cx-phase-line { flex: 1; height: 2px; background: repeating-linear-gradient(90deg, var(--border) 0 6px, transparent 6px 12px); }
        .cx-phase:last-child .cx-phase-line { display: none; }
        .cx-phase-card { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 22px; height: calc(100% - 62px); }
        .cx-phase-card h3 { font-size: 1.05rem; font-weight: 700; margin: 14px 0 8px; }
        .cx-phase-card p { margin: 0; font-size: 14px; line-height: 1.7; color: var(--muted); }
        .cx-note { display: flex; align-items: center; gap: 10px; margin-top: 28px; padding: 14px 18px; border-radius: 14px; background: rgba(2,142,196,0.09); border: 1px solid rgba(2,142,196,0.25); font-size: 14.5px; }

        /* modes */
        .cx-split { display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 56px; align-items: center; }
        .cx-split-rev { grid-template-columns: 1.1fr 0.9fr; }
        .cx-mode { border-radius: 18px; padding: 26px; border: 1px solid var(--border); background: var(--card); }
        .cx-mode + .cx-mode { margin-top: 16px; }
        .cx-mode-brand { background: linear-gradient(140deg, #036A94 0%, ${BRAND} 100%); border-color: transparent; color: #fff; }
        .cx-mode h3 { font-size: 1.25rem; font-weight: 700; margin: 0; }
        .cx-mode p { font-size: 14.5px; line-height: 1.7; margin: 12px 0 16px; color: var(--muted); }
        .cx-mode-brand p { color: rgba(255,255,255,0.86); }
        .cx-mode ul { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; }
        .cx-mode li { display: flex; align-items: flex-start; gap: 8px; font-size: 13.5px; font-weight: 500; line-height: 1.45; }

        /* domains */
        .cx-domains { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        .cx-domain { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 24px; }
        .cx-domain-head { display: flex; align-items: center; gap: 12px; padding-bottom: 16px; margin-bottom: 8px; border-bottom: 1px solid var(--border); }
        .cx-domain-head h3 { font-size: 1.02rem; font-weight: 700; margin: 0; }
        .cx-domain ul { list-style: none; margin: 0; padding: 0; }
        .cx-domain li { display: flex; align-items: center; gap: 10px; padding: 9px 0; font-size: 14.5px; font-weight: 500; }

        /* deliverables */
        .cx-deliv { display: grid; gap: 4px; margin-top: 8px; }
        .cx-deliv-row { display: flex; gap: 16px; padding: 16px 0; border-bottom: 1px solid var(--border); }
        .cx-deliv-row:last-child { border-bottom: none; }
        .cx-deliv-row h3 { font-size: 1rem; font-weight: 700; margin: 2px 0 4px; }
        .cx-deliv-row p { margin: 0; font-size: 14px; line-height: 1.65; color: var(--muted); }

        /* steps */
        .cx-steps { position: relative; display: grid; gap: 28px; }
        .cx-step { display: flex; gap: 18px; position: relative; }
        .cx-step:not(:last-child)::after { content: ""; position: absolute; left: 22px; top: 52px; bottom: -28px; width: 2px; background: repeating-linear-gradient(180deg, var(--border) 0 6px, transparent 6px 12px); }
        .cx-step h3 { font-size: 1.1rem; font-weight: 700; margin: 2px 0 6px; }
        .cx-step p { margin: 0; font-size: 14.5px; line-height: 1.7; color: var(--muted); }

        /* testimonials */
        .cx-marquee { overflow: hidden; position: relative; }
        .cx-marquee::before, .cx-marquee::after { content: ""; position: absolute; top: 0; bottom: 0; width: 90px; z-index: 2; pointer-events: none; }
        .cx-marquee::before { left: 0; background: linear-gradient(to right, var(--alt), transparent); }
        .cx-marquee::after { right: 0; background: linear-gradient(to left, var(--alt), transparent); }
        @keyframes cx-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .cx-track { display: flex; gap: 20px; width: max-content; animation: cx-scroll 50s linear infinite; padding: 6px 0; }
        .cx-track:hover { animation-play-state: paused; }
        .cx-tcard { width: 330px; flex-shrink: 0; background: var(--card); border: 1px solid var(--border); border-radius: 18px; padding: 24px; display: flex; flex-direction: column; justify-content: space-between; }
        .cx-avatar { width: 40px; height: 40px; border-radius: 50%; background: rgba(2,142,196,0.15); color: var(--brand); font-weight: 700; font-size: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

        /* faq */
        .cx-faq { max-width: 820px; margin: 0 auto; display: grid; gap: 12px; }
        .cx-faq-item { background: var(--card); border: 1px solid var(--border); border-radius: 14px; transition: border-color .2s; }
        .cx-faq-item[data-open="true"] { border-color: rgba(2,142,196,0.5); }
        .cx-faq-q { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; text-align: left; background: none; border: none; color: var(--text); font: inherit; font-weight: 600; font-size: 15.5px; padding: 18px 22px; cursor: pointer; border-radius: 14px; }
        .cx-faq-a { margin: 0; padding: 0 22px 20px; color: var(--muted); font-size: 14.5px; line-height: 1.75; }

        /* final cta */
        .cx-final { position: relative; overflow: hidden; border-radius: 26px; padding: 64px 40px; text-align: center; color: #fff; background: linear-gradient(135deg, #04314A 0%, #036A94 55%, ${BRAND} 100%); }
        .cx-final::before { content: ""; position: absolute; width: 380px; height: 380px; right: -120px; top: -160px; border-radius: 50%; background: rgba(255,169,0,0.22); filter: blur(60px); }
        .cx-final > * { position: relative; }
        .cx-final h2 { font-size: clamp(1.8rem, 3.6vw, 2.7rem); font-weight: 800; margin: 0 0 14px; }
        .cx-final p { max-width: 540px; margin: 0 auto 30px; font-size: 16px; line-height: 1.75; color: rgba(255,255,255,0.86); }

        @media (max-width: 1000px) {
          .cx-hero-grid, .cx-split, .cx-split-rev { grid-template-columns: 1fr; gap: 44px; }
          .cx-ledger, .cx-domains { grid-template-columns: repeat(2, 1fr); }
          .cx-phases { grid-template-columns: repeat(2, 1fr); }
          .cx-phase:nth-child(2) .cx-phase-line { display: none; }
          .cx-float { left: 12px; bottom: -22px; }
          .cx-float-badge { right: 12px; }
        }
        @media (max-width: 640px) {
          .cx-section { padding: 64px 0; }
          .cx-hero { padding: 56px 0 56px; }
          .cx-stats { grid-template-columns: repeat(2, 1fr); }
          .cx-stat { padding: 18px; flex-direction: column; align-items: flex-start; gap: 10px; }
          .cx-ledger, .cx-domains, .cx-phases, .cx-mode ul { grid-template-columns: 1fr; }
          .cx-phase-line { display: none; }
          .cx-phase-card { height: auto; }
          .cx-final { padding: 48px 22px; }
          .cx-tcard { width: 280px; }
        }
        @media (prefers-reduced-motion: reduce) { .cx-track { animation: none; } .cx-btn { transition: none; } }
      `}</style>

      <div className="cx" style={vars}>

        {/* ── HERO ───────────────────────────────────────────────────────────── */}
        <header className="cx-hero">
          <div className="cx-wrap">
            <div className="cx-hero-grid">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <span className="cx-pill"><Icon name="building" size={15} sw={2} /> Institutional training partnership</span>
                <h1>Campus Career Accelerator</h1>
                <p className="cx-hero-sub">
                  Industry-aligned training programs that take your students from classroom knowledge to job-ready skills. Delivered online or on campus, in 60 to 90 days, with a curriculum that covers technology, aptitude and core computer science.
                </p>
                <ul className="cx-audience">
                  {AUDIENCE.map(a => (
                    <li key={a}><Icon name="check" size={16} color={BRAND} sw={2.6} />{a}</li>
                  ))}
                </ul>
                <div className="cx-cta-row">
                  <div ref={buttonRef} id="google-calendar-button" />
                  <BookButton variant="yellow" />
                </div>
              </motion.div>

              <motion.div className="cx-hero-visual" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
                <Photo src={IMAGES.hero} alt="Students learning together in a college training session" ratio="5 / 4" />
                <div className="cx-float-badge"><Icon name="award" size={16} sw={2.2} /> Certificate on completion</div>
                <div className="cx-float">
                  <IconTile name="users" size={44} />
                  <div>
                    <div className="cx-display" style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>700+ students trained</div>
                    <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 3 }}>across engineering, degree and MCA programs</div>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="cx-stats">
              {STATS.map(s => (
                <div className="cx-stat" key={s.l}>
                  <IconTile name={s.icon} size={48} />
                  <div>
                    <div className="cx-stat-v">{s.v}</div>
                    <div className="cx-stat-l">{s.l}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* ── WHY US ─────────────────────────────────────────────────────────── */}
        <section className="cx-section">
          <div className="cx-wrap">
            <SectionHead
              title="A curriculum built for today's job market"
              lead="We close the gap between what is taught in the classroom and what companies expect on day one, so your students are ready for interviews, internships and their first job."
            />
            <div className="cx-ledger">
              {PILLARS.map(p => (
                <div className="cx-pillar" key={p.title}>
                  <IconTile name={p.icon} />
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PROGRAM STRUCTURE ──────────────────────────────────────────────── */}
        <section className="cx-section cx-alt">
          <div className="cx-wrap">
            <SectionHead
              title="How the program is structured"
              lead="Each program moves through four phases, from a shared foundation to placement readiness. We adjust the depth of each phase to your batch and your calendar."
            />
            <div className="cx-phases">
              {PHASES.map((p, i) => (
                <div className="cx-phase" key={p.title}>
                  <div className="cx-phase-top">
                    <div className="cx-phase-num">{i + 1}</div>
                    <div className="cx-phase-line" />
                  </div>
                  <div className="cx-phase-card">
                    <IconTile name={p.icon} size={40} color={i % 2 ? "#D98A00" : BRAND} />
                    <h3>{p.title}</h3>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="cx-note">
              <Icon name="clock" size={20} color={BRAND} />
              <span><strong>Duration: 60 to 90 days.</strong> Scheduled around your academic calendar and exam periods.</span>
            </div>
          </div>
        </section>

        {/* ── DELIVERY MODES ─────────────────────────────────────────────────── */}
        <section className="cx-section">
          <div className="cx-wrap">
            <div className="cx-split">
              <Photo src={IMAGES.classroom} alt="Trainer teaching in a college classroom" ratio="4 / 5" />
              <div>
                <SectionHead title="Learn online, or we come to your campus" lead="Choose the delivery mode that suits your institution. Both follow the same curriculum and assessment standards." />
                {MODES.map(m => (
                  <div key={m.title} className={`cx-mode ${m.tone === "brand" ? "cx-mode-brand" : ""}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: m.tone === "brand" ? "rgba(255,255,255,0.18)" : "rgba(2,142,196,0.12)", color: m.tone === "brand" ? "#fff" : BRAND }}>
                        <Icon name={m.icon} size={22} />
                      </div>
                      <h3>{m.title}</h3>
                    </div>
                    <p>{m.desc}</p>
                    <ul>
                      {m.points.map(pt => (
                        <li key={pt}><Icon name="check" size={16} color={m.tone === "brand" ? AMBER : BRAND} sw={2.8} />{pt}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── DOMAINS ────────────────────────────────────────────────────────── */}
        <section className="cx-section cx-alt">
          <div className="cx-wrap">
            <SectionHead
              title="15+ domains to choose from"
              lead="Pick one domain or combine several. We can also customise the track for each branch."
            />
            <div className="cx-domains">
              {DOMAIN_GROUPS.map(g => (
                <div className="cx-domain" key={g.title}>
                  <div className="cx-domain-head">
                    <IconTile name={g.icon} size={40} />
                    <h3>{g.title}</h3>
                  </div>
                  <ul>
                    {g.items.map(item => (
                      <li key={item}><Icon name="check" size={15} color={BRAND} sw={2.8} />{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── DELIVERABLES ───────────────────────────────────────────────────── */}
        <section className="cx-section">
          <div className="cx-wrap">
            <div className="cx-split cx-split-rev">
              <div>
                <SectionHead
                  title="Ready for placements, not just exams"
                  lead="Every program ends with projects, mock tests and interview practice. Your faculty and Training & Placement cell stay informed at every stage."
                />
                <div className="cx-deliv">
                  {DELIVERABLES.map(d => (
                    <div className="cx-deliv-row" key={d.title}>
                      <IconTile name={d.icon} size={42} color={AMBER === "#FFA900" ? "#D98A00" : AMBER} />
                      <div>
                        <h3>{d.title}</h3>
                        <p>{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Photo src={IMAGES.group} alt="Students working on laptops during a workshop" ratio="4 / 5" />
            </div>
          </div>
        </section>

        {/* ── PROCESS ────────────────────────────────────────────────────────── */}
        <section className="cx-section cx-alt">
          <div className="cx-wrap">
            <div className="cx-split">
              <Photo src={IMAGES.workshop} alt="Students collaborating at a training workshop" ratio="1 / 1" />
              <div>
                <SectionHead title="How we partner with your college" lead="From the first call to certified students, in four clear steps." />
                <div className="cx-steps">
                  {STEPS.map((s, i) => (
                    <div className="cx-step" key={s.title}>
                      <div className="cx-phase-num">{i + 1}</div>
                      <div>
                        <h3>{s.title}</h3>
                        <p>{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ───────────────────────────────────────────────────── */}
        <section className="cx-section">
          <div className="cx-wrap">
            <SectionHead center title="What colleges and students say" lead="Feedback from faculty and students who have been through a GrapplTech campus training." />
          </div>
          <div className="cx-marquee" style={{ background: "transparent" }}>
            <div className="cx-track">
              {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
                <figure key={i} className="cx-tcard" style={{ margin: 0 }} aria-hidden={i >= TESTIMONIALS.length}>
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                      <div style={{ display: "flex", gap: 3 }}>
                        {[...Array(5)].map((_, s) => <Icon key={s} name="star" size={14} color={AMBER} fill={AMBER} sw={1} />)}
                      </div>
                      <span style={{ color: BRAND, opacity: 0.35 }}><Icon name="quote" size={24} /></span>
                    </div>
                    <blockquote style={{ margin: "0 0 20px", fontSize: 14.5, lineHeight: 1.75 }}>{t.quote}</blockquote>
                  </div>
                  <figcaption style={{ display: "flex", alignItems: "center", gap: 12, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
                    <div className="cx-avatar">{t.name.replace(/^(Dr\.|Prof\.)\s*/, "").split(" ").map(w => w[0]).slice(0, 2).join("")}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>{t.role}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────────────────── */}
        <section className="cx-section cx-alt">
          <div className="cx-wrap">
            <SectionHead center title="Questions colleges ask us" lead="Can't find what you need? Email us and we'll get back to you." />
            <div className="cx-faq">
              {FAQS.map((f, i) => (
                <FaqItem key={f.q} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ──────────────────────────────────────────────────────── */}
        <section className="cx-section" style={{ paddingBottom: 100 }}>
          <div className="cx-wrap">
            <div className="cx-final">
              <div style={{ display: "inline-flex", width: 60, height: 60, borderRadius: 16, background: "rgba(255,255,255,0.15)", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                <Icon name="award" size={30} color="#fff" sw={1.6} />
              </div>
              <h2>Bring GrapplTech to your campus</h2>
              <p>Tell us your branches, batch size and goals. We'll plan a 60–90 day program that gets your students job-ready.</p>
              <div className="cx-cta-row" style={{ justifyContent: "center" }}>
                <a href="https://tally.so/r/obeQab" target="_blank" rel="noopener noreferrer" className="cx-btn cx-btn-white">
                  Book Your Appointment <Icon name="arrow" size={16} sw={2.4} />
                </a>
                <a href="mailto:trainings@grappl-tech.in" className="cx-btn cx-btn-line">
                  <Icon name="mail" size={16} sw={2} /> trainings@grappl-tech.in
                </a>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}